import { useState, type FC } from 'react'
import type { ResponsiveImageType } from 'react-datocms'
import styled from 'styled-components'

import { Container } from '@/components/shared/Container'
import { StandardButton } from '@/components/shared/StandardButton'
import { TrackedLink } from '@/components/shared/TrackedLink'

import { DatoImage } from '@/elements/global/DatoImage'

import { formatDisplayDate } from '@/lib/utils/dateFormatting'

type ArticleStub = {
  id?: string
  articleSlug: string
  articleTitle: string
  abstract: string
  featuredImage: { responsiveImage: ResponsiveImageType | null }
  _publishedAt: string
}

const StyledFeaturedArticleTitle = styled.h2`
  font-size: 40px;
  font-family: ${(props) => props.theme.v2.font.nantes};
  line-height: 54px;
  letter-spacing: -0.1px;
  @media screen and (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0;
  }
`

const StyledFeaturedArticlesGrid = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto;
  gap: 24px 24px;
  margin-bottom: 48px;
  @media screen and (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`
const StyledFeaturedArticle = styled.div`
  a {
    color: ${(props) => props.theme.v2.onSurface.text};
  }
  p {
    margin: 12px 0 0;
    padding: 0;
  }
  h3 {
    margin: 12px 0 0;
  }
  img {
    border-radius: 8px;
    object-fit: cover;
  }
`

const StyledButtonContainer = styled.div`
  text-align: center;
  margin-bottom: 42px;
`

const ArticlePreview: FC<{ article: ArticleStub }> = ({ article }) => {
  return (
    <StyledFeaturedArticle>
      <TrackedLink href={`/learn/${article.articleSlug}`} passHref>
        {article.featuredImage.responsiveImage && <DatoImage data={article.featuredImage.responsiveImage} />}
        <p className="small secondary">{formatDisplayDate(article._publishedAt)}</p>
        <h3>{article.articleTitle}</h3>
        <p className="small secondary">{article.abstract.slice(0, 110)}...</p>
      </TrackedLink>
    </StyledFeaturedArticle>
  )
}

const INITIAL_NO_ARTICLES = 3
const SHOW_MORE_NO_ARTICLES = 6

const getInitialViewCount = (showAll: boolean, numberOfArticles: number) => {
  return showAll ? numberOfArticles : INITIAL_NO_ARTICLES
}

type FeaturedArticlesProps = {
  articles: ArticleStub[]
  showAll?: boolean
  title?: string
  showViewMoreButton?: boolean
}

export const FeaturedArticles: FC<FeaturedArticlesProps> = ({
  articles,
  showAll = false,
  title = 'Featured articles',
  showViewMoreButton = false,
}) => {
  const [viewCount, setViewCount] = useState(getInitialViewCount(showAll, articles.length))
  const articleElements = articles.map((a) => <ArticlePreview key={a.articleSlug} article={a} />)
  const showViewMore = showViewMoreButton && viewCount < articleElements.length
  const handleViewMoreButton = () =>
    setViewCount(
      viewCount + SHOW_MORE_NO_ARTICLES < articleElements.length
        ? viewCount + SHOW_MORE_NO_ARTICLES
        : articleElements.length,
    )

  return (
    <Container>
      <hr />
      <StyledFeaturedArticleTitle>{title}</StyledFeaturedArticleTitle>

      <StyledFeaturedArticlesGrid>{articleElements.slice(0, viewCount)}</StyledFeaturedArticlesGrid>

      {showViewMore && (
        <StyledButtonContainer>
          <StandardButton btnStyle="bright-border" onClick={handleViewMoreButton}>
            View more
          </StandardButton>
        </StyledButtonContainer>
      )}
    </Container>
  )
}
